import Tooltip from "@kea-mod/common/tooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Exercise } from "../../../types/Exercise";
import type { Schema } from "../../../types/Schema";
import { ApiContext } from "../../../context/KeaAPI/newApi";
import { SchemaTreeGraph } from "../../Schemas/SchemaTree/SchemaTreeLogic";

type Props = {
  activeExercise: Exercise;
  h: Function;
};

export const SchemaField = (props: Props) => {
  const { schemaApi, schemaTreeApi, gradingServiceApi } = useContext(ApiContext);

  const { t } = useTranslation();

  // control the data loading state and only create the graph if all data is loaded
  const [dataLoaded, setDataLoaded] = useState(false);
  // custom dropdown open state (replaces the native select for a form-styled list with per-option info)
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([schemaApi.getAll(), schemaTreeApi.getAll(), gradingServiceApi.getAll()]).then(() => {
      setDataLoaded(true); // Update the state here
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the custom dropdown when clicking outside of it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Create a graph from the schema and grading services, but only if all data is loaded
  const graph = dataLoaded
    ? new SchemaTreeGraph([...schemaApi.elements, ...gradingServiceApi.elements], schemaTreeApi.elements)
    : new SchemaTreeGraph([], []);
  const rootSchemas = Array.from(graph.roots) as Schema[];

  const selectedSchema = rootSchemas.find(
    (schema: Schema) => schema.id === parseInt(props.activeExercise.schemaId),
  );

  const selectSchema = (option: Schema) => {
    // Mimic a native select change event so the shared handleChange can update schemaId.
    props.h({ target: { name: "schemaId", value: option.id.toString() } });
    setIsOpen(false);
  };

  return (
    <div className="field">
      <label className="label" htmlFor="schema">
        {t("schema")}
        <Tooltip
          icon="circle-info"
          class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
          content={t("schemaTooltip")}
        ></Tooltip>
      </label>
      <div className={`dropdown ${isOpen ? "is-active" : ""}`} style={{ width: "100%" }} ref={dropdownRef}>
        <div className="dropdown-trigger" style={{ width: "100%" }}>
          <button
            type="button"
            id="schema"
            className="input"
            aria-haspopup="true"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              cursor: "pointer",
              textAlign: "left",
              appearance: "none",
              WebkitAppearance: "none",
            }}
            onClick={() => setIsOpen((open) => !open)}
          >
            <span style={{ color: selectedSchema ? undefined : "#7a7a7a" }}>
              {selectedSchema ? selectedSchema.name : t("selectSchema")}
            </span>
            <span className="icon is-small" style={{ marginLeft: "0.5rem" }}>
              &#9662;
            </span>
          </button>
        </div>
        <div className="dropdown-menu" role="menu" style={{ width: "100%" }}>
          <div className="dropdown-content" style={{ maxHeight: "14rem", overflowY: "auto" }}>
            {rootSchemas.map((option) => (
              <div
                key={option.id}
                className="dropdown-item"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <span style={{ flex: 1, cursor: "pointer" }} onClick={() => selectSchema(option)}>
                  {option.name}
                </span>
                {option.description ? (
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-left"
                    content={option.description}
                  ></Tooltip>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
      {selectedSchema && selectedSchema.description ? <p className="help">{selectedSchema.description}</p> : null}
    </div>
  );
};
